// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledButton } from "@insite/mobius/Clickable";
import { FormFieldClickable, FormFieldIcon } from "@insite/mobius/FormField";
import { IconWrapper } from "@insite/mobius/Icon";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { xBaseTheme } from "@synd/XDL/types";
import { color, ellipsis, getSystemColor } from "@synd/XDL/Utilities";
import { css } from "styled-components";

const PlaceholderSelector = `
        &::placeholder,
        &::-webkit-input-placeholder
`;

export const placeholderStyle = css`
    ${PlaceholderSelector} {
        font-size: ${fontConfig.small.size}px;
        font-style: italic;
        letter-spacing: 0;
        line-height: 20px !important;
        max-width: 100%;
        color: ${getSystemColor("common.ui4")};
        font-weight: ${fontConfig.weight.regular};
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        border: "rounded",
        sizeVariant: "default",
        backgroundColor: "common.ui1",
        labelProps: {
            mergeCss: true,
            size: fontConfig.xSmallSingleLine.size,
            transform: "capitalize",
            weight: fontConfig.weight.regular,
            color: "text",
            css: css`
                min-height: initial;
                letter-spacing: 0;
                line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                display: flex;
                align-items: center;
                & .hint-wrapper {
                    margin-top: 1px;
                    display: flex;
                    flex-grow: 1;
                }
            `,
        },
        errorProps: {
            mergeCss: true,
            size: fontConfig.small.size,
            weight: fontConfig.weight.regular,
            color: "danger",
            css: css`
                min-height: initial;
                letter-spacing: 0;
                line-height: ${fontConfig.smallSingleLine.lineHeight};
            `,
        },
        hintProps: {
            mergeCss: true,
            size: fontConfig.small.size,
            weight: fontConfig.weight.regular,
            color: "text",
            css: css`
                min-height: initial;
                letter-spacing: 0;
                line-height: ${fontConfig.smallSingleLine.lineHeight};
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                width: ${({ labelPosition }: any) => (labelPosition === "left" ? "auto" : "100%")};
                input,
                select,
                textarea {
                    &:not(.react-datetime-picker__inputGroup__input) {
                        ${placeholderStyle}
                        &:disabled {
                            ${PlaceholderSelector} {
                                color: ${color("text.main")};
                            }
                        }
                        &:not(:disabled):not(:focus):hover {
                            border: 1px solid ${color("common.ui10")};
                        }
                    }
                }
                input {
                    &,
                    &:focus {
                        padding-right: attr(data-padding-right px, "12px");
                    }
                }
                ${FormFieldClickable} {
                    &:not(:disabled):hover {
                        ${FormFieldIcon} {
                            color: ${color("common.ui10")};
                        }
                    }
                }
                input,
                select {
                    &:not(.react-datetime-picker__inputGroup__input) {
                        ${ellipsis(1)}
                        text-overflow: ellipsis;
                    }
                }
                input,
                textarea {
                    box-shadow: inset 0 1px 0 0 ${color("common.ui7", 0.1)};
                    resize: vertical;
                }

                &[data-size="large"] {
                    && input {
                        padding: 0 16px 0 16px;
                    }

                    && ${IconWrapper} {
                        padding: 10px;
                    }
                }

                .end-adornment {
                    position: absolute;
                    top: 4px;
                    right: 4px;
                    display: flex;
                    align-items: center;
                }

                .clickable-wrapper {
                    position: absolute;
                    top: 0;
                    right: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 0 12px;
                    .divider {
                        width: 1px;
                        height: 24px;
                        background-color: ${color("common.ui3")};
                    }
                    & > ${FormFieldClickable} {
                        position: relative;
                        padding: 0;
                        width: 16px;
                        & ${FormFieldIcon} {
                            padding: 0;
                            width: auto;
                        }
                    }
                }

                &:has(${StyledButton}[data-test-selector="clear"]) {
                    input:not(.react-datetime-picker__inputGroup__input) {
                        &,
                        &:focus {
                            padding-right: 60px;
                        }
                    }
                }
            `,
            formField: css`
                ${({ labelPosition }: any) => {
                    if (labelPosition === "left") {
                        return css`
                            align-items: baseline;
                        `;
                    }
                }}
            `,
        },
    },
} as xBaseTheme["formField"];
